/**
 * SpecialtyPharmacyDetailsSection - Output section for specialty pharmacy details
 *
 * Displays specialty pharmacy details as a top-level accordion section.
 */

import { AccordionContent, AccordionItem, AccordionTrigger } from '@components/ui/accordion';
import React from 'react';

import { FieldPathProvider } from '../context';
import { Field, SectionTriggerScore } from './Field';

interface SpecialtyPharmacyDetailsSectionProps {
  data?: Record<string, unknown>;
  sectionId: string;
}

export const SpecialtyPharmacyDetailsSection: React.FC<SpecialtyPharmacyDetailsSectionProps> = ({
  data,
  sectionId,
}) => {
  return (
    <AccordionItem value={sectionId} className="border-b">
      <AccordionTrigger className="px-4 py-3 text-sm font-medium hover:bg-muted/50">
        <div className="flex items-center gap-2 flex-1">
          <span>Drug Specialty Pharmacy Details</span>
          <SectionTriggerScore subsectionKey="specialty_pharmacy_details" />
        </div>
      </AccordionTrigger>
      <AccordionContent className="px-4 pb-4">
        <FieldPathProvider prefix="drug.specialty_pharmacy_details">
          <div className="grid grid-cols-2 gap-x-4 gap-y-1">
            <Field
              label="SP Name"
              value={data?.specialty_pharmacy_name1}
              fieldId="specialty_pharmacy_name1"
            />
            <Field
              label="Phone"
              value={data?.specialty_pharmacy_phone1}
              fieldId="specialty_pharmacy_phone1"
            />
            <Field
              label="Fax"
              value={data?.specialty_pharmacy_fax1}
              fieldId="specialty_pharmacy_fax1"
            />
            <Field
              label="Network Status"
              value={data?.specialty_pharmacy_network_status}
              fieldId="specialty_pharmacy_network_status"
            />
            <Field
              label="SP Allowed"
              value={data?.is_specialty_pharmacy_allowed}
              fieldId="is_specialty_pharmacy_allowed"
            />
            <Field
              label="SP Type"
              value={data?.specialty_pharmacy_type}
              fieldId="specialty_pharmacy_type"
            />
            <Field
              label="Exclusions Exist"
              value={data?.specialty_pharmacy_exclusions_exist}
              fieldId="specialty_pharmacy_exclusions_exist"
            />
            <Field
              label="Buy and Bill Allowed"
              value={data?.buy_and_bill_allowed}
              fieldId="buy_and_bill_allowed"
            />
            {data?.specialty_pharmacy_exclusions != null && (
              <div className="col-span-2">
                <Field
                  label="Exclusions"
                  value={data.specialty_pharmacy_exclusions}
                  fieldId="specialty_pharmacy_exclusions"
                />
              </div>
            )}
            {data?.reason_for_buy_and_billed_not_allowed != null && (
              <div className="col-span-2">
                <Field
                  label="Buy/Bill Not Allowed Reason"
                  value={data.reason_for_buy_and_billed_not_allowed}
                  fieldId="reason_for_buy_and_billed_not_allowed"
                />
              </div>
            )}
            <Field
              label="BnB Any Exclusions or Limitations Exists"
              value={data?.buy_and_bill_exclusions_limitations_exists}
              fieldId="buy_and_bill_exclusions_limitations_exists"
            />
            {data?.buy_and_bill_exclusions_limitations != null && (
              <div className="col-span-2">
                <Field
                  label="BnB Any Exclusions or Limitations"
                  value={data.buy_and_bill_exclusions_limitations}
                  fieldId="buy_and_bill_exclusions_limitations"
                />
              </div>
            )}
            <Field
              label="SP Name 2"
              value={data?.specialty_pharmacy_name2}
              fieldId="specialty_pharmacy_name2"
            />
            <Field
              label="Phone 2"
              value={data?.specialty_pharmacy_phone2}
              fieldId="specialty_pharmacy_phone2"
            />
            <Field
              label="Fax 2"
              value={data?.specialty_pharmacy_fax2}
              fieldId="specialty_pharmacy_fax2"
            />
            <Field
              label="SP Name 3"
              value={data?.specialty_pharmacy_name3}
              fieldId="specialty_pharmacy_name3"
            />
            <Field
              label="Phone 3"
              value={data?.specialty_pharmacy_phone3}
              fieldId="specialty_pharmacy_phone3"
            />
            <Field
              label="Fax 3"
              value={data?.specialty_pharmacy_fax3}
              fieldId="specialty_pharmacy_fax3"
            />
            <Field label="REMS Status 1" value={data?.rems_status1} fieldId="rems_status1" />
            <Field label="REMS Status 2" value={data?.rems_status2} fieldId="rems_status2" />
            <Field label="REMS Status 3" value={data?.rems_status3} fieldId="rems_status3" />
            <Field
              label="Exception Process Available"
              value={data?.specialty_pharmacy_exception_process_available}
              fieldId="specialty_pharmacy_exception_process_available"
            />
            <div className="col-span-2">
              <Field
                label="Exception Process Details"
                value={data?.specialty_pharmacy_exception_process_details}
                fieldId="specialty_pharmacy_exception_process_details"
              />
            </div>
          </div>
        </FieldPathProvider>
      </AccordionContent>
    </AccordionItem>
  );
};
